import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { AVIS, COLOR_MAP, NOTE_GOOGLE, NB_AVIS_GOOGLE } from '../../data/avis';

const reviews = AVIS;
const colorMap = COLOR_MAP;

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  // Responsive items per view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else setItemsPerView(3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setCurrentIndex((prev) => (prev + 1) % reviews.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);

  // Visible reviews (cyclic)
  const visible = [];
  for (let i = 0; i < itemsPerView; i++) {
    visible.push(reviews[(currentIndex + i) % reviews.length]);
  }

  return (
    <section className="py-16 md:py-28 px-4 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Ce que disent nos clients</h2>
          <p className="text-base md:text-lg text-gray-600">Des avis 100% authentiques laissés sur Google</p>
        </div>

        <div className="relative">
          {/* Navigation arrows */}
          <button
            onClick={prev}
            aria-label="Avis précédent"
            data-testid="testimonials-prev-btn"
            className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-full p-2 md:p-3 shadow-lg transition-all hover:scale-110"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
          </button>
          <button
            onClick={next}
            aria-label="Avis suivant"
            data-testid="testimonials-next-btn"
            className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 z-10 bg-white hover:bg-gray-100 border-2 border-gray-200 rounded-full p-2 md:p-3 shadow-lg transition-all hover:scale-110"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
          </button>

          {/* Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 px-8 md:px-12">
            {visible.map((review, idx) => (
              <Card
                key={`${currentIndex}-${idx}`}
                className="hover-lift card-hover transition-all duration-500 fade-in"
                data-testid={`testimonial-card-${idx}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={`star-${review.name}-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    {/* Google G badge */}
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-label="Avis Google">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                  </div>
                  <p className="text-gray-700 mb-4 italic min-h-[96px]">« {review.text} »</p>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${colorMap[review.color]}`}>
                      {review.initial}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{review.name}</div>
                      <div className="text-sm text-gray-500">{review.date}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-8">
            {reviews.map((review, idx) => (
              <button
                key={`dot-${review.name}`}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Aller à l'avis ${idx + 1}`}
                data-testid={`testimonial-dot-${idx}`}
                className={`h-2 rounded-full transition-all ${
                  idx === currentIndex ? 'w-8 bg-blue-600' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <div className="inline-flex items-center gap-2 text-gray-600 mb-6">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            <span className="text-2xl font-bold text-gray-900">{NOTE_GOOGLE.replace(".0", "")}/5</span>
            <span className="text-gray-500">• {NB_AVIS_GOOGLE} avis Google</span>
          </div>

          <div>
            <a
              href="https://share.google/PHMyc4K1Px9kMzRxQ"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-all-google-reviews-btn"
              className="inline-flex items-center gap-3 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300 px-8 py-4 rounded-xl font-bold text-lg transition-all hover:shadow-xl hover:scale-105"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span>Voir tous les avis Google</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
